import { Controller } from "@hotwired/stimulus"

import { formatGermanDate, isoFromUtcDate, parseGermanDate, todayIso } from "../utils/date_format"
import { positionPanel, trackPosition } from "../utils/popover_panel"

// Typeable date field backed by a Cally calendar.
//
// The visible input shows d.m.Y and can be typed into; a hidden companion carries the ISO
// value and the real form field name. This mirrors the altInput model the flatpickr
// controllers used, so the wire format is unchanged (always YYYY-MM-DD).
//
// The panel uses the native popover API so it renders in the top layer — an absolutely
// positioned panel gets clipped inside the Plantafel drawer and the offcanvas.
export default class extends Controller {
  static targets = ["display", "value", "calendar", "panel", "trigger", "legend", "time"]

  static values = {
    holidays: { type: Object, default: {} },
    markedDates: { type: Array, default: [] },
  }

  connect() {
    this.repositionPanel = this.repositionPanel.bind(this)
    this.stopTracking = null
    this.emitting = false

    this.syncCalendarFromValue()
  }

  disconnect() {
    this.stopTrackingPosition()
  }

  // --- opening and closing -------------------------------------------------

  toggle() {
    if (this.panelTarget.matches(":popover-open")) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.decorateDays()
    this.syncCalendarFromValue()
    this.refreshLegend()
    this.panelTarget.showPopover()
    this.repositionPanel()
    this.startTrackingPosition()
  }

  close() {
    if (this.panelTarget.matches(":popover-open")) this.panelTarget.hidePopover()
    this.stopTrackingPosition()
  }

  // Clicking the field opens the calendar, as it did when the input was readonly. The caret
  // still lands where you clicked and typing keeps working, because showing the popover does
  // not move focus.
  //
  // Bound to click rather than focus on purpose: tabbing through a form with several date
  // fields should not pop a calendar open at each one.
  openFromInput() {
    if (this.panelTarget.matches(":popover-open")) return

    this.open()
  }

  // Fires for both programmatic and user-driven popover state changes.
  panelToggled(event) {
    if (event.newState === "closed") this.stopTrackingPosition()
  }

  // --- value plumbing ------------------------------------------------------

  // The user typed. Keep the hidden field in step on every keystroke that yields a valid
  // date, but never clobber what they are still typing.
  displayChanged() {
    const raw = this.displayTarget.value.trim()

    if (raw === "") {
      this.writeValue("")
      this.displayTarget.setCustomValidity("")
      return
    }

    const iso = parseGermanDate(raw)
    if (iso) {
      this.writeValue(iso)
      this.syncCalendarFromValue()
      this.displayTarget.setCustomValidity("")
    }
  }

  // On blur, normalise what survived ("1.2.26" becomes "01.02.2026") or flag it. An
  // unparseable entry is left visible so the user can correct it rather than silently wiped.
  displayCommitted() {
    const raw = this.displayTarget.value.trim()

    if (raw === "") {
      this.writeValue("")
      this.displayTarget.setCustomValidity("")
      return
    }

    const iso = parseGermanDate(raw)
    if (iso) {
      this.displayTarget.value = formatGermanDate(iso)
      this.writeValue(iso)
      this.displayTarget.setCustomValidity("")
    } else {
      this.displayTarget.setCustomValidity(this.invalidMessage)
    }
  }

  // Enter selects and closes rather than submitting the surrounding form.
  displayKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.displayCommitted()
      this.close()
    } else if (event.key === "Escape" && this.panelTarget.matches(":popover-open")) {
      event.preventDefault()
      this.close()
    }
  }

  // Cally emits `change` only for user-driven selection, so this cannot loop.
  calendarChanged() {
    const iso = this.calendarTarget.value || ""

    this.writeDisplay(formatGermanDate(iso))
    this.writeValue(iso)
    this.close()
    this.displayTarget.focus()
  }

  // Something outside the picker wrote to the hidden field and dispatched `change` — e.g. the
  // sick-note document extraction, or a sibling field copying its value across. Pull the new
  // value into the visible input and the calendar.
  //
  // Our own writeValue() dispatches on the very field this listens to, so an echo has to be
  // ignored outright. Comparing values is not enough: mid-typing the two legitimately differ
  // ("1.2.20" against the "01.02.2020" it just parsed to), and rewriting the input there ate
  // the rest of the date — "1.2.2026" ended up as "01.02.202026" while 2020-02-01 was quietly
  // left in the hidden field.
  valueChangedExternally() {
    if (this.emitting) return

    this.syncTimeFromValue()
    const expected = formatGermanDate(this.currentIsoDate)
    if (this.displayTarget.value === expected) return

    this.writeDisplay(expected)
    this.syncCalendarFromValue()
  }

  // In label mode the visible half is a button showing something like "Juli 2026", so it has
  // no value to write and nothing to validate — those fields navigate rather than submit.
  writeDisplay(text) {
    if (this.displayTarget.tagName !== "INPUT") return

    this.displayTarget.value = text
    this.displayTarget.setCustomValidity("")
  }

  // The date half of the hidden value. On a datetime field the hidden value also carries the
  // time, which the calendar and the visible date input must not see.
  get currentIsoDate() {
    return (this.valueTarget.value || "").slice(0, 10)
  }

  // Filter toolbars submit as soon as a bound changes; wire with
  // `data-action="change->date-picker#submit"` on the field.
  submit() {
    this.element.closest("form")?.requestSubmit()
  }

  clear() {
    this.displayTarget.value = ""
    this.displayTarget.setCustomValidity("")
    this.writeValue("")
    this.syncCalendarFromValue()
    this.close()
  }

  selectToday() {
    this.calendarTarget.value = todayIso()
    this.calendarChanged()
  }

  // The companion time input on a datetime field.
  timeChanged() {
    if (this.syncingTime) return

    this.writeValue(this.currentIsoDate)
  }

  // On a datetime field an outside write carries the time too ("2026-11-02 07:30", from voice
  // input or an undo): hand it to the time picker, or its stale half would be composed back in on
  // the next edit and the written time lost. Its change event updates the visible time; our own
  // timeChanged ignores it, since the hidden value already holds exactly what was written.
  syncTimeFromValue() {
    if (!this.hasTimeTarget) return

    const field = this.timeTarget.querySelector('[data-time-picker-target="value"]')
    const time = (this.valueTarget.value || "").slice(11, 16)
    if (!field || field.value === time) return

    field.value = time
    this.syncingTime = true
    try {
      field.dispatchEvent(new Event("change", { bubbles: true }))
    } finally {
      this.syncingTime = false
    }
  }

  // Datetime fields submit "YYYY-MM-DD HH:MM" — the format the appointment and log-book
  // controllers have always received, and deliberately not datetime-local's "…THH:MM".
  composeValue(iso) {
    if (!this.hasTimeTarget || iso === "") return iso

    return `${iso} ${this.currentTime || "00:00"}`
  }

  // The time half lives in a nested TimeFieldComponent, whose own hidden field carries the
  // "HH:MM". It is nameless, so only this composed value is ever posted.
  get currentTime() {
    return this.timeTarget.querySelector('[data-time-picker-target="value"]')?.value || ""
  }

  // Writing to `value` directly would not notify anything listening on the real form field,
  // so dispatch the events a plain input would.
  writeValue(isoDate) {
    const iso = this.composeValue(isoDate)
    if (this.valueTarget.value === iso) return

    this.valueTarget.value = iso
    // Bracketed so our own valueChangedExternally can tell this round trip from a genuine
    // outside write. dispatchEvent is synchronous, so the flag covers exactly the echo.
    this.emitting = true
    try {
      this.valueTarget.dispatchEvent(new Event("input", { bubbles: true }))
      this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }))
    } finally {
      this.emitting = false
    }
  }

  syncCalendarFromValue() {
    if (!this.hasCalendarTarget) return

    const iso = this.currentIsoDate
    if (this.calendarTarget.value !== iso) this.calendarTarget.value = iso
    if (iso) this.calendarTarget.focusedDate = iso
  }

  // --- day decoration ------------------------------------------------------

  // Cally appends whatever getDayParts returns to each day button's `part` attribute, so
  // holidays and marked days are styled with plain ::part() rules.
  //
  // Two things make this fussy, both verified against Cally 0.9.2:
  //
  // 1. It must be set on <calendar-date>/<calendar-range>, NOT on <calendar-month>. The
  //    parent is the context provider and the month only consumes it; assigning to the month
  //    creates a dead own-property (its prototype has no accessor) and nothing renders.
  // 2. It must be set after the element is live. Assigning during connect() stores the value
  //    but schedules no re-render. So this runs on open, when the element is mounted.
  //
  // A fresh closure is assigned every time: Atomico skips an assignment of the same reference,
  // so reusing one would make the marked dates impossible to update. Reading the values here
  // rather than closing over them at connect is what lets a sibling controller change them —
  // the tasks form rewrites the marked range whenever the site changes.
  decorateDays() {
    if (!this.hasCalendarTarget) return

    const holidays = this.holidaysValue
    const marked = new Set(this.markedDatesValue)
    // Cleared rather than skipped: a field whose marked dates went from populated to empty
    // (the site was deselected) would otherwise keep the previous closure and keep the marks.
    if (Object.keys(holidays).length === 0 && marked.size === 0) {
      this.calendarTarget.getDayParts = undefined
      return
    }

    const getDayParts = (date) => {
      const iso = isoFromUtcDate(date)
      const parts = []
      if (holidays[iso]) parts.push("holiday")
      if (marked.has(iso)) parts.push("marked")
      return parts.join(" ")
    }

    this.calendarTarget.getDayParts = getDayParts
  }

  // Lists the public holidays falling in the visible month under the grid. Cally has no
  // supported hook for per-day tooltips, and matching its shadow-DOM buttons by their
  // locale-formatted aria-label would break on any upgrade — a legend is both safe and
  // easier to spot than a hover target.
  refreshLegend() {
    if (!this.hasLegendTarget) return

    const holidays = this.holidaysValue
    const month = this.visibleMonth
    const entries = Object.keys(holidays)
      .filter((iso) => iso.startsWith(month))
      .sort()

    this.legendTarget.replaceChildren()
    this.legendTarget.classList.toggle("hidden", entries.length === 0)
    if (entries.length === 0) return

    for (const iso of entries) {
      const row = document.createElement("div")
      row.className = "flex items-baseline gap-1.5"

      const dot = document.createElement("span")
      dot.className = "shrink-0 text-red-600"
      dot.textContent = "●"

      const label = document.createElement("span")
      // textContent, not innerHTML — holiday names are data, not markup.
      label.textContent = `${this.dayMonthFormatter.format(new Date(`${iso}T00:00:00Z`))} — ${holidays[iso]}`

      row.append(dot, label)
      this.legendTarget.append(row)
    }
  }

  // The month Cally is currently showing, as "YYYY-MM".
  get visibleMonth() {
    const focused = this.calendarTarget?.focusedDate || this.currentIsoDate
    return (focused || todayIso()).slice(0, 7)
  }

  // The calendar's own locale attribute (server-rendered: de-AT for Austrian tenants), so the
  // holiday legend names months exactly as the grid does.
  get dayMonthFormatter() {
    this.formatter ||= new Intl.DateTimeFormat(this.calendarLocale || "de-DE", {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    })
    return this.formatter
  }

  get calendarLocale() {
    return this.hasCalendarTarget ? this.calendarTarget.getAttribute("locale") : null
  }

  // --- positioning ---------------------------------------------------------

  // The popover lives in the top layer, so it is positioned against the viewport by
  // positionPanel — see utils/popover_panel, shared with the time picker.
  //
  // Anchored to the date input, not the trigger and not the wrapper: the icon is too narrow to
  // align a calendar against, and on a datetime field the wrapper also spans the time input,
  // which would push the panel below it.
  repositionPanel() {
    positionPanel(this.displayTarget, this.panelTarget)
  }

  startTrackingPosition() {
    this.stopTrackingPosition()
    this.stopTracking = trackPosition(this.repositionPanel)
  }

  stopTrackingPosition() {
    this.stopTracking?.()
    this.stopTracking = null
  }

  get invalidMessage() {
    return this.displayTarget.dataset.invalidMessage || ""
  }
}
